import { Controller } from '@hotwired/stimulus'
import dayjs, { type Dayjs } from 'dayjs'
import dayJsArraySupport from 'dayjs/plugin/arraySupport'
import flatpickr from 'flatpickr'
import type { Instance } from 'flatpickr/dist/types/instance'
import { broadcastInputChange } from 'packs/ui/app/components/ui/_input/_broadcast_change_utils'

import weekOfYear from 'dayjs/plugin/weekOfYear'

type InputEventCustom = InputEvent & { target: HTMLInputElement }

const FOCUS_CLASSES = ['border-viaorange']

/* controller */

export default class extends Controller<HTMLDivElement> {
  static targets = ['input', 'hidden', 'pickr', 'weekNumber', 'clear']

  declare readonly hasHiddenTarget: boolean
  declare readonly hiddenTarget: HTMLInputElement

  declare readonly inputTargets: HTMLInputElement[]
  declare readonly hasInputTarget: boolean

  declare readonly pickrTarget: HTMLDivElement
  declare readonly hasPickrTarget: boolean

  declare readonly weekNumberTarget: HTMLSpanElement
  declare readonly hasWeekNumberTarget: boolean

  declare readonly clearTarget: HTMLButtonElement
  declare readonly hasClearTarget: boolean

  static values = {
    format: String,
    defaultValue: String,
    minDateAllowed: String,
    maxDateAllowed: String,
    hasWeekNumbers: Boolean,
    isDisabled: Boolean
  }

  declare formatValue: string
  declare readonly hasFormatValue: boolean
  declare defaultValueValue: string
  declare readonly hasDefaultValueValue: boolean
  declare minDateAllowedValue: string
  declare readonly hasMinDateAllowedValue: boolean
  declare maxDateAllowedValue: string
  declare readonly hasMaxDateAllowedValue: boolean
  declare isDisabledValue: boolean
  declare readonly hasIsDisabledValue: boolean
  declare hasWeekNumbersValue: boolean

  flatpickr: null | Instance = null
  formatToUse: string = 'YYYY-MM-DD'

  #siblingFieldFocus(currentField: HTMLInputElement, increment: 1 | -1 = 1): void {
    const currentFieldIndex = this.inputTargets.findIndex(input => input === currentField)
    const siblingField = this.inputTargets[currentFieldIndex + increment]
    siblingField?.focus()
  }

  #populateInputs(): void {
    if (this.hasDefaultValueValue && this.defaultValueValue !== '') {
      const dateRegExp = /(\d{4})-(\d{2})-(\d{2})/
      const datetimeRegExp = /(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})/
      const regExp = this.formatValue === 'date' ? dateRegExp : datetimeRegExp

      const matches = this.defaultValueValue.match(regExp)
      if (matches === null) return
      matches.slice(1).forEach((value, index) => {
        this.inputTargets[index].value = value
      })

      this.#changeWeekDisplay(dayjs(this.defaultValueValue))
    }
  }

  #populateHiddenAndFlatpickr(): void {
    const fieldValues = this.inputTargets.map((input, index) => {
      if (index === 1) return parseInt(input.value, 10) - 1
      return input.value
    })
    const dayForDate = dayjs(fieldValues as dayjs.ConfigType)
    const dateIsValid = dayForDate.isValid()
    if (dateIsValid) {
      this.hiddenTarget.value = dayForDate.format(this.formatToUse)
      this.flatpickr?.setDate(dayForDate.toDate(), false, this.formatToUse)
      broadcastInputChange(this.hiddenTarget)
      if (this.hasClearTarget) this.clearTarget.classList.remove('hidden')
    }
    this.#changeWeekDisplay(dayForDate)
  }

  #initFlatpickr(): void {
    // first, checks for existing calendars and removes them (necessary for cloned fields)
    const currentInitialisedCalendars = this.element.querySelectorAll('.flatpickr-calendar')
    if (currentInitialisedCalendars.length > 0) {
      currentInitialisedCalendars.forEach(c => {
        c.remove()
      })
    }

    this.flatpickr = flatpickr(this.element, {
      time_24hr: true,
      allowInput: true,
      defaultHour: 0,
      inline: true,
      appendTo: this.pickrTarget,
      disableMobile: true,
      weekNumbers: this.hasWeekNumbersValue,
      enableTime: this.formatValue === 'datetime',
      maxDate: this.maxDateAllowedValue,
      minDate: this.minDateAllowedValue,
      minuteIncrement: 1,
      onChange: d => {
        this.#pickDate(d)
      }
    })
  }

  #positionPickrPopover(): void {
    if (!this.hasPickrTarget) return

    // reposition popover
    const triggerRect = this.element.getBoundingClientRect()
    this.pickrTarget.style.top = `${triggerRect.bottom}px`
    this.pickrTarget.style.left = `${triggerRect.left}px`

    // if popover is too much on the bottom, reposition it on top of the trigger
    const spaceForDropdownV = window.innerHeight - this.element.getBoundingClientRect().bottom
    if (this.pickrTarget.clientHeight > spaceForDropdownV) {
      this.pickrTarget.style.top = `${triggerRect.top - this.pickrTarget.clientHeight}px`
    }
    // if popover is too much on the right, reposition it aligned to the left of the trigger
    const spaceForDropdownH = window.innerWidth - this.element.getBoundingClientRect().right
    if (this.pickrTarget.clientWidth > spaceForDropdownH) {
      this.pickrTarget.style.left = `${triggerRect.right - this.pickrTarget.clientWidth}px`
    }
  }

  #pickDate(selectedDates: Date[]): void {
    const date = dayjs(selectedDates[0])
    if (date === undefined) return

    // populate hidden field
    const dayForDate = dayjs(date as dayjs.ConfigType)
    const dateIsValid = dayForDate.isValid()
    if (dateIsValid) {
      this.hiddenTarget.value = dayForDate.format(this.formatToUse)
      broadcastInputChange(this.hiddenTarget)
      if (this.hasClearTarget) this.clearTarget.classList.remove('hidden')
    }

    const valuesToSet = [date.year(), date.month() + 1, date.date()]
    if (this.formatValue === 'datetime') {
      valuesToSet.push(date.hour(), date.minute())
    }

    this.inputTargets.forEach((input, index) => {
      input.value = String(valuesToSet[index]).padStart(2, '0')
    })

    if (this.formatValue !== 'datetime') {
      this.pickrTarget.hidePopover()
    }
    this.#changeWeekDisplay(dayForDate)
  }

  #clearDate(): void {
    this.hiddenTarget.value = ''
    broadcastInputChange(this.hiddenTarget)
    if (this.hasClearTarget) this.clearTarget.classList.add('hidden')
  }

  #changeWeekDisplay(dateAsDay: Dayjs): void {
    if (this.hasWeekNumbersValue) {
      this.weekNumberTarget.innerText = String(dayjs(dateAsDay).week())
    }
  }

  connect(): void {
    if (this.hasWeekNumbersValue) dayjs.extend(weekOfYear)
    this.formatToUse = this.formatValue === 'date' ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm'
    dayjs.extend(dayJsArraySupport)

    this.#populateInputs()
    if (!this.isDisabledValue) this.#initFlatpickr()
    this.#populateHiddenAndFlatpickr()
  }

  focus(): void {
    this.element.classList.add(...FOCUS_CLASSES)
    this.pickrTarget.showPopover()
    this.#positionPickrPopover()
    // hide when click outside
    document.addEventListener('click', e => {
      if (!this.element.contains(e.target as Node)) {
        this.pickrTarget.hidePopover()
      }
    })
  }

  blur(): void {
    this.element.classList.remove(...FOCUS_CLASSES)
  }

  keydown(e?: KeyboardEvent): void {
    if (e?.key.length === 1 && !/[0-9]/.test(e?.key)) {
      e?.preventDefault()
    }
    if (e?.key === 'Backspace') {
      const input = e.target as HTMLInputElement
      if (input.value === '') {
        this.#siblingFieldFocus(input, -1)
      }
    }
    if (e?.key === 'ArrowLeft') {
      const input = e.target as HTMLInputElement
      if (input.selectionStart === 0) {
        this.#siblingFieldFocus(input, -1)
      }
    }
    if (e?.key === 'ArrowRight') {
      const input = e.target as HTMLInputElement
      if (input.selectionStart === input.maxLength) {
        this.#siblingFieldFocus(input, 1)
      }
    }
  }

  input(e?: InputEventCustom): void {
    const input = e?.target
    if (input === undefined) return

    // pass to next field if input is complete

    const isCursorAtEndOfField = input.maxLength === input.value.length
    const isMonthField = input === this.inputTargets[1]
    const isCompleteMonthSingleDigit =
      isMonthField && input.value.length === 1 && /[2-9]/.test(input.value)

    if (isCursorAtEndOfField || isCompleteMonthSingleDigit) {
      this.#siblingFieldFocus(input, 1)
    }

    // populate hidden field and pickr if all fields are complete and valid

    const allFieldsAreComplete = this.inputTargets.every((input, index) => {
      if (index === 1) return input.value.length === 1 || input.value.length === 2
      return input.value.length === input.maxLength
    })
    if (allFieldsAreComplete) {
      this.#populateHiddenAndFlatpickr()
    } else {
      const emptyFields = this.inputTargets.filter(i => i.value === '')
      if (emptyFields.length > 0) this.#clearDate()
    }
  }

  clickClear(): void {
    this.inputTargets.forEach(input => {
      input.value = ''
    })
    this.#clearDate()
  }
}
